import { useSyncExternalStore } from "react";
import { censorText } from "@yay/shared";
import { getStickerSrc, subscribeStickers, getStickerSnapshot } from "./StickerPicker";
import { useCensorPref } from "../lib/censorPref";

/** Render chat text with stickers + @mention highlights. */
export function ChatText({
  text,
  meUsername,
  compact,
  stickersOnly = false,
  resolveUser,
}: {
  text: string;
  meUsername?: string | null;
  /** Smaller stickers for reply quotes / bubbles. */
  compact?: boolean;
  /**
   * Only swap sticker tokens for images — leave everything else as plain text
   * so speech/floating bubbles keep their existing look.
   */
  stickersOnly?: boolean;
  resolveUser?: (username: string) => { color?: string; avatarSeed?: string } | null;
}) {
  useSyncExternalStore(subscribeStickers, getStickerSnapshot);
  const censorOn = useCensorPref();
  // Keep @mentions intact even when glued to punctuation: @cat! → @cat + !
  const parts = text.split(/(@[A-Za-z0-9_.-]+)|(\s+)/u);
  const me = meUsername?.toLowerCase() ?? "";

  return (
    <>
      {parts.map((part, i) => {
        if (!part) return null;
        // Visual-only profanity filter — the mention check runs on the raw
        // token so highlight still works for censored usernames.
        const shown = censorOn ? censorText(part) : part;

        const src = getStickerSrc(part);
        if (src) {
          return (
            <img
              key={i}
              className={`chat-sticker${compact ? " compact" : ""}`}
              src={src}
              alt={shown}
            />
          );
        }

        if (stickersOnly) {
          return <span key={i}>{shown}</span>;
        }

        // Persist highlight for any @token — not only currently-online users.
        if (part.startsWith("@") && part.length > 1) {
          const lower = part.slice(1).toLowerCase();
          const isMe = me !== "" && lower === me;
          const isEveryone = lower === "everyone";
          const u = isEveryone ? null : resolveUser?.(part.slice(1));
          return (
            <span
              key={i}
              className={`chat-mention${isMe ? " chat-mention-me" : ""}${
                isEveryone ? " chat-everyone" : ""
              }`}
              style={
                !isMe && !isEveryone && u?.color
                  ? { color: u.color }
                  : undefined
              }
            >
              {shown}
            </span>
          );
        }

        return (
          <span key={i} className="chat-text">
            {shown}
          </span>
        );
      })}
    </>
  );
}
